import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * Tier → color palette (web). Returns hex strings we can use inline.
 * Tiers mirror backend: excellent / good / needs_attention / action_needed / critical
 */
export function tierColors(tier, dark) {
  // Bright fintech greens for excellent/good; warm colors for lower tiers.
  const light = {
    excellent:       { ring: '#0FC862', ringEnd: '#059669', track: '#F3F4F6', fg: '#059669', glow: true },
    good:            { ring: '#0FC862', ringEnd: '#0FC862', track: '#F3F4F6', fg: '#059669', glow: false },
    needs_attention: { ring: '#F59E0B', ringEnd: '#F59E0B', track: '#F3F4F6', fg: '#B45309', glow: false },
    action_needed:   { ring: '#F97316', ringEnd: '#F97316', track: '#F3F4F6', fg: '#C2410C', glow: false },
    critical:        { ring: '#EF4444', ringEnd: '#EF4444', track: '#F3F4F6', fg: '#B91C1C', glow: false },
  };
  const darkPal = {
    excellent:       { ring: '#22DD8A', ringEnd: '#10B981', track: '#1F1F1F', fg: '#22DD8A', glow: true },
    good:            { ring: '#22DD8A', ringEnd: '#22DD8A', track: '#1F1F1F', fg: '#22DD8A', glow: false },
    needs_attention: { ring: '#F59E0B', ringEnd: '#F59E0B', track: '#1F1F1F', fg: '#F59E0B', glow: false },
    action_needed:   { ring: '#FB923C', ringEnd: '#FB923C', track: '#1F1F1F', fg: '#FB923C', glow: false },
    critical:        { ring: '#EF4444', ringEnd: '#EF4444', track: '#1F1F1F', fg: '#EF4444', glow: false },
  };
  return (dark ? darkPal : light)[tier] || (dark ? darkPal.good : light.good);
}

/**
 * Ring gauge — SVG donut with tier gradient + optional green glow.
 * size: outer diameter in px.
 */
export function ScoreRing({ score, tier, size = 160, stroke = 12, dark }) {
  const c = tierColors(tier, dark);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const dash = `${circ * pct} ${circ * (1 - pct)}`;
  const gradId = `scoreGrad-${tier}`;
  const filterId = `scoreGlow-${tier}`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="score-ring">
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c.ring} />
          <stop offset="100%" stopColor={c.ringEnd} />
        </linearGradient>
        {c.glow ? (
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        ) : null}
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r}
        stroke={c.track} strokeWidth={stroke} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r}
        stroke={`url(#${gradId})`} strokeWidth={stroke} fill="none"
        strokeLinecap="round"
        strokeDasharray={dash}
        filter={c.glow ? `url(#${filterId})` : undefined}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 900ms cubic-bezier(0.22, 1, 0.36, 1)' }} />
    </svg>
  );
}

/**
 * Score card, used on Dashboard and Bookkeeper strip. `compact` shrinks the ring
 * and puts the label beside instead of below.
 */
export default function ScoreCard({ score, colors, compact = false, dark, linkTo = '/bookkeeper', progress = null }) {
  if (!score) return null;
  const c = tierColors(score.tier, dark);
  const size = compact ? 88 : 168;
  const stroke = compact ? 8 : 12;
  const total = score.issues?.total ?? 0;

  const inner = (
    <div className={compact ? 'flex items-center gap-5' : 'flex flex-col sm:flex-row items-center gap-8'}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <ScoreRing score={score.score} tier={score.tier} size={size} stroke={stroke} dark={dark} />
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className={`font-bold tabular-nums leading-none tracking-tight ${compact ? 'text-3xl' : 'text-6xl'}`}
            data-testid="score-value"
            style={{ color: c.fg, letterSpacing: '-0.03em' }}>
            {score.score}
          </span>
          <span className={`tabular-nums font-semibold ${compact ? 'text-[10px]' : 'text-xs'}`}
            style={{ color: colors.textMuted, marginTop: compact ? 2 : 6 }}>
            / 100
          </span>
        </div>
      </div>

      <div className={`flex-1 min-w-0 ${compact ? '' : 'text-center sm:text-left'}`}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] mb-1.5"
          style={{ color: colors.textMuted }}>
          Clasifi Score
        </p>
        <p className={`font-semibold tracking-tight ${compact ? 'text-base' : 'text-xl'}`}
          data-testid="score-label"
          style={{ color: colors.text, letterSpacing: '-0.01em' }}>
          {score.label}
        </p>
        {progress ? (
          <p className="text-xs mt-1.5 tabular-nums" data-testid="score-progress-line"
            style={{ color: colors.textMuted }}>
            {progress.reviewed} / {progress.total} reviewed
          </p>
        ) : null}
        <div className={`flex items-center gap-2 mt-3 ${compact ? '' : 'justify-center sm:justify-start'}`}>
          <span
            data-testid="score-issue-chip"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold tabular-nums"
            style={{ backgroundColor: c.track, color: c.fg }}>
            {total} open issue{total === 1 ? '' : 's'}
            {linkTo ? <ChevronRight size={12} /> : null}
          </span>
          {score.issues.high > 0 ? (
            <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: colors.textMuted }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.error }} />
              {score.issues.high} high
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo}
        data-testid="score-card"
        className="block rounded-2xl border p-6 transition-all hover:border-opacity-100 focus-visible:outline-none focus-visible:ring-2"
        style={{
          backgroundColor: colors.surface,
          borderColor: colors.border,
          transition: 'border-color 200ms, box-shadow 200ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}>
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid="score-card"
      className="rounded-2xl border p-6"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
      {inner}
    </div>
  );
}
